import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardPagination from '../components/DashboardPagination';
import emendaService, { type EmendaDTO } from '../services/emendaService';
import execucaoFinanceiraService, { type ExecucaoFinanceiraResumo } from '../services/execucaoFinanceiraService';
import documentoLiberacaoService, { type DocumentoLiberacao } from '../services/documentoLiberacaoService';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/user.types';
import { UI_THEME } from '../ui/theme';
import { getErrorMessage } from '../services/api';
import { FIELD_CLASS } from '../components/ui/fieldStyles';
import Button from '../components/ui/Button';
import { Table, Tbody, Td, Th, Thead, Tr } from '../components/ui/Table';

type ExecucaoRow = {
  emenda: EmendaDTO;
  resumo: ExecucaoFinanceiraResumo;
  documento: DocumentoLiberacao;
};

const money = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const errorMessage = getErrorMessage;

const identificacao = (emenda: EmendaDTO) =>
  emenda.officialCode || (emenda.numeroEmenda ? `${emenda.numeroEmenda}/${emenda.exercicio ?? ''}` : emenda.id);

const etapaOperacional = ({ resumo, documento }: ExecucaoRow) => {
  if (!resumo.liberada) return { key: 'PLANO_PENDENTE', label: 'Plano pendente' };
  if (resumo.empenhadoLiquido <= 0) return { key: 'AGUARDANDO_EMPENHO', label: 'Aguardando empenho' };
  // O documento de liberação é uma etapa paralela (a instituição pode enviar a
  // nota fiscal a qualquer momento) — ela só aparece como pendência enquanto a
  // liquidação realmente não avançou. Uma vez que a Coplan (ou lançamento
  // manual) já liquidou/pagou, a etapa de documento não deve mais mascarar o
  // progresso financeiro real: uma etapa não barra a outra.
  if (resumo.liquidadoLiquido < resumo.empenhadoLiquido) {
    if (documento.status === 'NAO_APLICAVEL') return { key: 'AGUARDANDO_LIQUIDACAO', label: 'Aguardando liquidação' };
    if (documento.status === 'NAO_SOLICITADO') return { key: 'DOCUMENTO_NAO_SOLICITADO', label: 'Solicitar documento' };
    if (documento.status === 'AGUARDANDO_ENVIO') return { key: 'AGUARDANDO_DOCUMENTO', label: documento.vencido ? 'Nota fiscal vencida' : 'Aguardando nota fiscal' };
    if (documento.status === 'DEVOLVIDO_CORRECAO') return { key: 'CORRECAO_DOCUMENTO', label: 'Aguardando correção' };
    if (documento.status === 'EM_ANALISE') return { key: 'DOCUMENTO_EM_ANALISE', label: 'Documento em análise' };
    return { key: 'AGUARDANDO_LIQUIDACAO', label: 'Aguardando liquidação' };
  }
  if (resumo.pagoLiquido < resumo.liquidadoLiquido) return { key: 'AGUARDANDO_PAGAMENTO', label: 'Aguardando pagamento' };
  return { key: 'PAGA', label: 'Paga' };
};

const ancoraOperacional = (row: ExecucaoRow) => [
  'DOCUMENTO_NAO_SOLICITADO', 'AGUARDANDO_DOCUMENTO', 'CORRECAO_DOCUMENTO', 'DOCUMENTO_EM_ANALISE',
].includes(etapaOperacional(row).key) ? 'documento-liberacao' : 'execucao-financeira';

const ExecucaoFinanceiraPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const [rows, setRows] = useState<ExecucaoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [situacao, setSituacao] = useState('TODAS');
  const [transferencia, setTransferencia] = useState('TODAS');
  const [exercicio, setExercicio] = useState('TODOS');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const emendas = await emendaService.listWithDetails();
        const results = await Promise.allSettled(
          emendas
            .filter((emenda) => emenda.id)
            .map(async (emenda) => {
              const [resumo, documento] = await Promise.all([
                execucaoFinanceiraService.getResumo(String(emenda.id)),
                documentoLiberacaoService.get(String(emenda.id)),
              ]);
              return { emenda, resumo, documento };
            }),
        );
        if (!cancelled) {
          setRows(results.flatMap((result) => result.status === 'fulfilled' ? [result.value] : []));
        }
      } catch (err: unknown) {
        if (!cancelled) setError(errorMessage(err, 'Não foi possível carregar a execução financeira.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const sincronizarComCoplan = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const resumo = await execucaoFinanceiraService.sincronizarComCoplan(false);
      const referencias = (resumo.contratosCriados ?? 0) + (resumo.contratosAtualizados ?? 0) + (resumo.atosLegaisCriados ?? 0) + (resumo.atosLegaisAtualizados ?? 0);
      setSyncMessage(`Sincronização concluída: ${resumo.created} criado(s), ${resumo.updated} atualizado(s), ${resumo.skipped} pulado(s)${referencias ? `; contratos e atos legais: ${resumo.contratosCriados ?? 0} contrato(s) novo(s), ${resumo.contratosAtualizados ?? 0} atualizado(s), ${resumo.atosLegaisCriados ?? 0} ato(s) legal(is) novo(s)` : ''}${resumo.warnings.length ? `, ${resumo.warnings.length} aviso(s)` : ''}.`);
      setReloadKey((key) => key + 1);
    } catch (err: unknown) {
      setSyncMessage(errorMessage(err, 'Não foi possível sincronizar com o Coplan.'));
    } finally {
      setSyncing(false);
    }
  };

  const anos = useMemo(() => Array.from(new Set(rows.map(({ emenda }) => emenda.exercicio).filter(Boolean) as number[]))
    .sort((a, b) => b - a), [rows]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('pt-BR');
    return rows.filter(({ emenda, resumo, documento }) => {
      const matchesQuery = !normalizedQuery || [
        identificacao(emenda), emenda.institutionName, emenda.councilorName,
        emenda.description, emenda.objectDetail, emenda.secretariaDestino,
      ].some((value) => String(value ?? '').toLocaleLowerCase('pt-BR').includes(normalizedQuery));
      const matchesSituacao = situacao === 'TODAS' || etapaOperacional({ emenda, resumo, documento }).key === situacao;
      const matchesTransferencia = transferencia === 'TODAS'
        || String(emenda.tipoTransferencia ?? '').trim().toUpperCase() === transferencia;
      const matchesExercicio = exercicio === 'TODOS' || String(emenda.exercicio) === exercicio;
      return matchesQuery && matchesSituacao && matchesTransferencia && matchesExercicio;
    });
  }, [exercicio, query, rows, situacao, transferencia]);

  useEffect(() => { setPage(1); }, [query, situacao, transferencia, exercicio]);

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);
  const totais = useMemo(() => rows.reduce((acc, row) => {
    const etapa = etapaOperacional(row).key;
    return {
      planosPendentes: acc.planosPendentes + (etapa === 'PLANO_PENDENTE' ? 1 : 0),
      documentosPendentes: acc.documentosPendentes + (['DOCUMENTO_NAO_SOLICITADO', 'AGUARDANDO_DOCUMENTO', 'CORRECAO_DOCUMENTO'].includes(etapa) ? 1 : 0),
      documentosEmAnalise: acc.documentosEmAnalise + (etapa === 'DOCUMENTO_EM_ANALISE' ? 1 : 0),
      pago: acc.pago + row.resumo.pagoLiquido,
    };
  }, { planosPendentes: 0, documentosPendentes: 0, documentosEmAnalise: 0, pago: 0 }), [rows]);

  const statusBadge = (row: ExecucaoRow) => {
    const etapa = etapaOperacional(row);
    const tone = etapa.key === 'PLANO_PENDENTE' || etapa.key === 'AGUARDANDO_DOCUMENTO'
      ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
      : etapa.key === 'PAGA'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300'
        : etapa.key === 'CORRECAO_DOCUMENTO' || row.documento.vencido
          ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
        : 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-300';
    return <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${tone}`}>{etapa.label}</span>;
  };

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border ${heroClass}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-sky-700 dark:text-sky-400'}`}>
              {isSaas ? 'Operação financeira' : 'Gestão orçamentária'}
            </p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl dark:text-slate-100">Execução Financeira</h1>
          </div>
          {hasRole(UserRole.ADMIN) && (
            <Button variant="secondary" onClick={sincronizarComCoplan} disabled={syncing} title="Puxa empenhos, liquidações e pagamentos já importados do Coplan; não reescreve o que já foi importado">
              {syncing ? 'Sincronizando...' : 'Sincronizar com Coplan'}
            </Button>
          )}
        </div>
        {syncMessage && <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{syncMessage}</p>}
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
          Acompanhe os saldos de todas as emendas visíveis e acesse empenhos, liquidações, pagamentos e anulações.
        </p>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4 dark:divide-slate-700">
            {[
              ['Planos pendentes', totais.planosPendentes, 'bg-amber-500'],
              ['Documentos pendentes', totais.documentosPendentes, 'bg-red-500'],
              ['Documentos em análise', totais.documentosEmAnalise, 'bg-sky-500'],
              ['Total pago', money(totais.pago), 'bg-teal-500'],
            ].map(([label, value, dot]) => (
              <div key={String(label)} className="min-h-[72px] px-4 py-3">
                <div className="flex items-center gap-2"><span className={`h-1.5 w-1.5 rounded-full ${dot}`} /><span className="text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{label}</span></div>
                <div className="mt-2 text-xl font-bold tabular-nums text-slate-950 dark:text-slate-100">{value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`rounded-3xl border p-4 ${surfaceClass}`}>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_220px_160px_160px_auto]">
          <label className="text-sm text-slate-600 dark:text-slate-300">Buscar
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Emenda, beneficiário, parlamentar ou objeto" className={`${FIELD_CLASS} mt-1`} />
          </label>
          <label className="text-sm text-slate-600 dark:text-slate-300">Situação
            <select value={situacao} onChange={(event) => setSituacao(event.target.value)} className={`${FIELD_CLASS} mt-1`}>
              <option value="TODAS">Todas</option><option value="PLANO_PENDENTE">Plano pendente</option><option value="AGUARDANDO_EMPENHO">Aguardando empenho</option><option value="DOCUMENTO_NAO_SOLICITADO">Solicitar documento</option><option value="AGUARDANDO_DOCUMENTO">Aguardando nota fiscal</option><option value="CORRECAO_DOCUMENTO">Aguardando correção</option><option value="DOCUMENTO_EM_ANALISE">Documento em análise</option><option value="AGUARDANDO_LIQUIDACAO">Aguardando liquidação</option><option value="AGUARDANDO_PAGAMENTO">Aguardando pagamento</option><option value="PAGA">Paga</option>
            </select>
          </label>
          <label className="text-sm text-slate-600 dark:text-slate-300">Transferência
            <select value={transferencia} onChange={(event) => setTransferencia(event.target.value)} className={`${FIELD_CLASS} mt-1`}>
              <option value="TODAS">Todas</option><option value="DIRETA">Direta</option><option value="INDIRETA">Indireta</option>
            </select>
          </label>
          <label className="text-sm text-slate-600 dark:text-slate-300">Exercício
            <select value={exercicio} onChange={(event) => setExercicio(event.target.value)} className={`${FIELD_CLASS} mt-1`}>
              <option value="TODOS">Todos</option>{anos.map((ano) => <option key={ano} value={ano}>{ano}</option>)}
            </select>
          </label>
          <Button variant="secondary" className="self-end" onClick={() => { setQuery(''); setSituacao('TODAS'); setTransferencia('TODAS'); setExercicio('TODOS'); }}>Limpar filtros</Button>
        </div>
      </section>

      {loading ? (
        <div className={`rounded-3xl border p-12 text-center text-sm text-slate-500 ${surfaceClass}`}>Carregando execuções financeiras...</div>
      ) : error ? (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center text-red-700 shadow-sm dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">{error}</div>
      ) : filtered.length === 0 ? (
        <div className={`rounded-3xl border p-12 text-center text-sm text-slate-500 ${surfaceClass}`}>Nenhuma execução encontrada com os filtros selecionados.</div>
      ) : isSaas ? (
        <section className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
          <div className="grid gap-4 p-4 lg:grid-cols-2 xl:grid-cols-3">
            {paged.map((row) => {
              const { emenda, resumo, documento } = row;
              return (
              <article key={emenda.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/60">
                <div className="flex items-start justify-between gap-3"><div><p className="text-xs font-semibold uppercase tracking-wide text-teal-700 dark:text-teal-300">{identificacao(emenda)}</p><h2 className="mt-1 font-semibold text-slate-900 dark:text-slate-100">{emenda.institutionName || 'Beneficiário não informado'}</h2></div>{statusBadge(row)}</div>
                <p className="mt-2 line-clamp-2 text-sm text-slate-600 dark:text-slate-300">{emenda.objectDetail || emenda.description || 'Objeto não informado'}</p>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Parlamentar: {emenda.councilorName || 'Não informado'}</p>
                {documento.prazoEnvio && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Prazo do documento: {new Date(`${documento.prazoEnvio}T12:00:00`).toLocaleDateString('pt-BR')}</p>}
                <div className="mt-4 grid grid-cols-3 gap-2 text-sm"><div><span className="block text-xs text-slate-500">Empenhado</span><strong>{money(resumo.empenhadoLiquido)}</strong></div><div><span className="block text-xs text-slate-500">Liquidado</span><strong>{money(resumo.liquidadoLiquido)}</strong></div><div><span className="block text-xs text-slate-500">Pago</span><strong>{money(resumo.pagoLiquido)}</strong></div></div>
                <Link to={`/dashboard/execucao-financeira/${emenda.id}#${ancoraOperacional(row)}`} className="mt-4 inline-flex rounded-xl bg-teal-600 px-3 py-2 text-sm font-semibold text-white hover:bg-teal-700">Abrir execução</Link>
              </article>
            );})}
          </div>
          <DashboardPagination page={page} pageSize={pageSize} totalItems={filtered.length} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} itemLabel="emendas" />
        </section>
      ) : (
        <section className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
          <div className="overflow-x-auto"><Table><Thead><Tr><Th>Emenda / beneficiário</Th><Th>Situação</Th><Th align="right">Orçado</Th><Th align="right">Empenhado</Th><Th align="right">Liquidado</Th><Th align="right">Pago</Th></Tr></Thead><Tbody>
            {paged.map((row) => {
              const { emenda, resumo } = row;
              const destino = `/dashboard/execucao-financeira/${emenda.id}#${ancoraOperacional(row)}`;
              const abrir = () => navigate(destino);
              return (
                <Tr key={emenda.id} role="link" tabIndex={0} aria-label={`Abrir execução financeira da emenda ${identificacao(emenda)}`} onClick={abrir} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); abrir(); } }} className="focus:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500 dark:focus:bg-slate-800/60">
                  <Td><strong className="text-slate-900 dark:text-slate-100">{identificacao(emenda)}</strong><span className="mt-1 block text-slate-600 dark:text-slate-300">{emenda.institutionName || 'Beneficiário não informado'}</span><span className="mt-0.5 block text-xs text-slate-500">Parlamentar: {emenda.councilorName || 'Não informado'}</span></Td>
                  <Td>{statusBadge(row)}</Td>
                  <Td align="right" className="tabular-nums">{money(resumo.valorOrcado)}</Td>
                  <Td align="right" className="tabular-nums">{money(resumo.empenhadoLiquido)}</Td>
                  <Td align="right" className="tabular-nums">{money(resumo.liquidadoLiquido)}</Td>
                  <Td align="right" className="tabular-nums">{money(resumo.pagoLiquido)}</Td>
                </Tr>
              );
            })}
          </Tbody></Table></div>
          <DashboardPagination page={page} pageSize={pageSize} totalItems={filtered.length} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} itemLabel="emendas" />
        </section>
      )}
    </div>
  );
};

export default ExecucaoFinanceiraPage;
